import { supabase } from './supabase';
import { existingRelationshipInviteAction } from './memberInviteLogic';
import { TrainerMemberRelationship, TrainerMemberRelationshipWithMember, User } from '../types/dataModel';
import { displayNameFromProfile } from '../pages/account/accountShared';

export type InviteMemberByEmailResult =
  | { kind: 'relationship'; relationship: TrainerMemberRelationshipWithMember; isResend: boolean }
  | { kind: 'pending'; email: string; isResend: boolean }
  | { kind: 'already_connected'; relationship: TrainerMemberRelationshipWithMember };

type CreateRelationshipInviteResult =
  | { outcome: 'new' | 'reopened' | 'resent'; relationship: TrainerMemberRelationshipWithMember }
  | { outcome: 'already_connected'; relationship: TrainerMemberRelationshipWithMember };

/** Supabase `users` columns embedded on trainer–member relationship queries. */
const TRAINER_MEMBER_USER_EMBED =
  'id, email, first_name, last_name, role, created_at, updated_at';

export class TrainerMemberService {
  private static mapMemberUserRow(memberData: any): User {
    return {
      id: memberData.id,
      email: memberData.email || '',
      first_name: typeof memberData.first_name === 'string' ? memberData.first_name : '',
      last_name: typeof memberData.last_name === 'string' ? memberData.last_name : '',
      role: memberData.role || 'member',
      createdAt: memberData.created_at || new Date().toISOString(),
      updatedAt: memberData.updated_at || new Date().toISOString(),
    };
  }

  private static mapRelationshipRow(item: any): TrainerMemberRelationshipWithMember {
    let memberData = item.member;
    if (Array.isArray(memberData)) memberData = memberData[0];
    return {
      id: item.id,
      trainerId: item.trainer_id,
      memberId: item.member_id,
      status: item.status,
      createdAt: item.created_at,
      startedAt: item.started_at,
      endedAt: item.ended_at,
      updatedAt: item.updated_at,
      requestedAt: item.requested_at,
      respondedAt: item.responded_at,
      lastContactDate: item.last_contact_date,
      nextCheckInDate: item.next_check_in_date,
      notes: item.notes,
      fitnessGoals: item.fitness_goals,
      initialAssessment: item.initial_assessment,
      contractType: item.contract_type,
      billingStatus: item.billing_status,
      monthlyRate: item.monthly_rate,
      assignedBy: item.assigned_by,
      terminationReason: item.termination_reason,
      memberRating: item.member_rating,
      memberReview: item.member_review,
      member: memberData ? this.mapMemberUserRow(memberData) : (null as any),
    };
  }
  /**
   * Get a single trainer–member relationship by trainer and member ID.
   * Use this instead of getTrainerMembers + find when you only need one member (e.g. Member detail page).
   */
  static async getTrainerMemberByMemberId(trainerId: string, memberId: string): Promise<TrainerMemberRelationshipWithMember | null> {
    try {
      const { data, error } = await supabase
        .from('trainer_member_relationships')
        .select(`
          *,
          member:users!member_id (${TRAINER_MEMBER_USER_EMBED})
        `)
        .eq('trainer_id', trainerId)
        .eq('member_id', memberId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching trainer member:', error);
        throw error;
      }

      if (!data) return null;

      return this.mapRelationshipRow(data);
    } catch (error) {
      console.error('Error in getTrainerMemberByMemberId:', error);
      throw error;
    }
  }

  /**
   * Get all members for a trainer
   */
  static async getTrainerMembers(trainerId: string): Promise<TrainerMemberRelationshipWithMember[]> {
    try {
      const { data, error } = await supabase
        .from('trainer_member_relationships')
        .select(`
          *,
          member:users!member_id (${TRAINER_MEMBER_USER_EMBED})
        `)
        .eq('trainer_id', trainerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching trainer members:', error);
        throw error;
      }

      console.log('📦 Raw data from getTrainerMembers:', JSON.stringify(data, null, 2));

      return (data || []).map((item: any) => {
        let memberData = item.member;
        if (Array.isArray(memberData)) memberData = memberData[0];
        if (!memberData && item.member_id) {
          console.log('⚠️ Member data missing from join, trainer_id:', item.trainer_id, 'member_id:', item.member_id);
        }
        console.log('🔍 Processing relationship:', {
          id: item.id,
          trainer_id: item.trainer_id,
          member_id: item.member_id,
          memberRaw: item.member,
          memberProcessed: memberData,
          memberName: memberData ? displayNameFromProfile(memberData) : undefined,
          memberEmail: memberData?.email,
        });
        return this.mapRelationshipRow(item);
      });
    } catch (error) {
      console.error('Error in getTrainerMembers:', error);
      throw error;
    }
  }

  /**
   * Find a user by email
   * Note: This requires RLS policy that allows trainers to query members by email
   */
  static async findUserByEmail(email: string): Promise<User | null> {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      // Try querying with .maybeSingle() first (doesn't throw error if no rows)
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, role, timezone, created_at, updated_at')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (error) {
        console.error('Error finding user by email:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        // Check if it's an RLS policy error
        if (error.code === '42501' || error.message?.includes('policy')) {
          throw new Error('Permission denied: Unable to query users. Please check RLS policies allow trainers to search members by email. Run database_fix_trainer_search_members_working.sql');
        }
        throw error;
      }

      if (!data) {
        return null;
      }

      return {
        id: data.id,
        email: data.email,
        first_name: typeof data.first_name === 'string' ? data.first_name : '',
        last_name: typeof data.last_name === 'string' ? data.last_name : '',
        role: data.role,
        timezone: data.timezone,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Error in findUserByEmail:', error);
      // Re-throw with more context if it's a known error
      if (error.message) {
        throw error;
      }
      throw new Error(`Failed to find user: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Invite by email: if user exists as member, create relationship; if no account, store pending invite.
   * Caller should send the appropriate email (connect vs signup) based on the result.
   */
  static async inviteMemberByEmail(
    trainerId: string,
    memberEmail: string,
    options?: {
      status?: 'active' | 'inactive' | 'paused' | 'terminated' | 'onboarding';
      contractType?: string;
      billingStatus?: string;
      monthlyRate?: number;
      notes?: string;
    }
  ): Promise<InviteMemberByEmailResult> {
    const email = memberEmail.trim().toLowerCase();
    const member = await this.findUserByEmail(email);

    if (member) {
      if (member.role !== 'member') {
        throw new Error(`User with email ${memberEmail} is not a member (role: ${member.role})`);
      }
      const created = await this.createRelationship(trainerId, email, {
        ...options,
        status: options?.status ?? 'onboarding',
      });
      if (created.outcome === 'already_connected') {
        return { kind: 'already_connected', relationship: created.relationship };
      }
      return {
        kind: 'relationship',
        relationship: created.relationship,
        isResend: created.outcome === 'resent',
      };
    }

    const { data: insertedNew, error: insertError } = await supabase.rpc('insert_trainer_pending_invite', {
      p_trainer_id: trainerId,
      p_email: email,
    });

    if (insertError) {
      console.error('Error saving pending invite:', insertError);
      throw insertError;
    }
    const isResend = insertedNew === false;
    return { kind: 'pending', email, isResend };
  }

  /**
   * Get pending invites (emails invited who haven't signed up yet)
   */
  static async getPendingInvites(trainerId: string): Promise<{ email: string; createdAt: string }[]> {
    const { data, error } = await supabase
      .from('trainer_pending_invites')
      .select('email, created_at')
      .eq('trainer_id', trainerId)
      .order('last_invited_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching pending invites:', error);
      return [];
    }
    return (data || []).map((row: { email: string; created_at: string }) => ({
      email: row.email,
      createdAt: row.created_at,
    }));
  }

  /**
   * Create or update a trainer–member relationship for an invite-by-email flow.
   * @see memberInviteLogic.existingRelationshipInviteAction
   */
  static async createRelationship(
    trainerId: string,
    memberEmail: string,
    options?: {
      status?: 'active' | 'inactive' | 'paused' | 'terminated' | 'onboarding';
      contractType?: string;
      billingStatus?: string;
      monthlyRate?: number;
      notes?: string;
    }
  ): Promise<CreateRelationshipInviteResult> {
    try {
      const member = await this.findUserByEmail(memberEmail);

      if (!member) {
        throw new Error('No account found with this email. They need to sign up as a member first—then you can add them using this email.');
      }

      if (member.role !== 'member') {
        throw new Error(`User with email ${memberEmail} is not a member (role: ${member.role})`);
      }

      const { data: existing } = await supabase
        .from('trainer_member_relationships')
        .select('id, status')
        .eq('trainer_id', trainerId)
        .eq('member_id', member.id)
        .maybeSingle();

      const relSelect = `*, member:users!member_id (${TRAINER_MEMBER_USER_EMBED})`;

      if (existing) {
        const act = existingRelationshipInviteAction(existing.status);

        if (act.action === 'blocked') {
          throw new Error(act.message);
        }

        if (act.action === 'already_connected') {
          const { data: full, error: fullErr } = await supabase
            .from('trainer_member_relationships')
            .select(relSelect)
            .eq('id', existing.id)
            .single();
          if (fullErr || !full) {
            console.error('Error loading relationship:', fullErr);
            throw fullErr || new Error('Relationship not found');
          }
          return { outcome: 'already_connected', relationship: this.mapRelationshipRow(full) };
        }

        if (act.action === 'resend') {
          const { data: updated, error: updateError } = await supabase
            .from('trainer_member_relationships')
            .update({
              requested_at: new Date().toISOString(),
            })
            .eq('id', existing.id)
            .select(relSelect)
            .single();

          if (updateError || !updated) {
            console.error('Error updating relationship (resend):', updateError);
            throw updateError || new Error('Failed to update relationship');
          }
          return { outcome: 'resent', relationship: this.mapRelationshipRow(updated) };
        }

        const { data: updated, error: updateError } = await supabase
          .from('trainer_member_relationships')
          .update({
            status: 'pending',
            requested_at: new Date().toISOString(),
            responded_at: null,
            contract_type: options?.contractType || undefined,
            billing_status: options?.billingStatus || 'free',
            monthly_rate: options?.monthlyRate || undefined,
            notes: options?.notes || undefined,
          })
          .eq('id', existing.id)
          .select(relSelect)
          .single();

        if (updateError || !updated) {
          console.error('Error updating relationship:', updateError);
          throw updateError || new Error('Failed to update relationship');
        }
        return { outcome: 'reopened', relationship: this.mapRelationshipRow(updated) };
      }

      const { data, error } = await supabase
        .from('trainer_member_relationships')
        .insert({
          trainer_id: trainerId,
          member_id: member.id,
          status: 'pending',
          contract_type: options?.contractType,
          billing_status: options?.billingStatus || 'free',
          monthly_rate: options?.monthlyRate,
          notes: options?.notes,
          requested_at: new Date().toISOString(),
        })
        .select(relSelect)
        .single();

      if (error || !data) {
        console.error('Error creating relationship:', error);
        throw error || new Error('Failed to create relationship');
      }

      return { outcome: 'new', relationship: this.mapRelationshipRow(data) };
    } catch (error) {
      console.error('Error in createRelationship:', error);
      throw error;
    }
  }

  /**
   * Update relationship status
   */
  static async updateRelationshipStatus(
    relationshipId: string,
    status: 'active' | 'inactive' | 'paused' | 'terminated' | 'onboarding' | 'pending' | 'rejected',
    terminationReason?: string
  ): Promise<void> {
    try {
      const updateData: any = { status };
      if (terminationReason) {
        updateData.termination_reason = terminationReason;
      }
      // Set responded_at when accepting or declining pending requests
      if (status === 'active' || status === 'inactive' || status === 'rejected') {
        updateData.responded_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('trainer_member_relationships')
        .update(updateData)
        .eq('id', relationshipId);

      if (error) {
        console.error('Error updating relationship status:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateRelationshipStatus:', error);
      throw error;
    }
  }

  /**
   * Re-activate an archived relationship: send a new connection request (status → pending).
   * Same effect as Add Member for this member: member must accept again.
   */
  static async reActivateConnection(relationshipId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('trainer_member_relationships')
        .update({
          status: 'pending',
          requested_at: new Date().toISOString(),
          responded_at: null,
        })
        .eq('id', relationshipId);

      if (error) {
        console.error('Error re-activating connection:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in reActivateConnection:', error);
      throw error;
    }
  }

  /**
   * Check if the member has at least one active or onboarding trainer relationship.
   * Used to show "Find a Trainer" vs "Contact your Trainer" in the no-upcoming-workouts banner.
   */
  static async getMemberHasActiveTrainer(memberId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('trainer_member_relationships')
        .select('id')
        .eq('member_id', memberId)
        .in('status', ['active', 'onboarding'])
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error checking active trainer:', error);
        return false;
      }
      return !!data;
    } catch (error) {
      console.error('Error in getMemberHasActiveTrainer:', error);
      return false;
    }
  }

  /**
   * Get pending connection requests for a member
   * Note: Returns relationships with trainer info (not member info)
   */
  static async getPendingRequests(memberId: string): Promise<(TrainerMemberRelationship & { trainer: User })[]> {
    try {
      // First try with the join
      const { data, error } = await supabase
        .from('trainer_member_relationships')
        .select(`
          *,
          trainer:users!trainer_id (
            id,
            email,
            first_name,
            last_name,
            role,
            created_at,
            updated_at
          )
        `)
        .eq('member_id', memberId)
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      if (error) {
        console.error('Error fetching pending requests:', error);
        throw error;
      }

      console.log('📦 Raw data from getPendingRequests:', JSON.stringify(data, null, 2));

      // If trainer data is missing from join, fetch it separately
      const results = await Promise.all((data || []).map(async (item: any) => {
        // Handle trainer data - it might be an object or array
        let trainerData = item.trainer;
        
        // If trainer is an array (shouldn't happen with foreign key, but handle it)
        if (Array.isArray(trainerData)) {
          trainerData = trainerData[0];
        }
        
        // If trainer data is missing, fetch it separately
        if (!trainerData && item.trainer_id) {
          console.log('⚠️ Trainer data missing from join, fetching separately for trainer_id:', item.trainer_id);
          try {
            // Use maybeSingle() instead of single() to handle RLS blocks gracefully
            const { data: trainerInfo, error: trainerError } = await supabase
              .from('users')
              .select('id, email, first_name, last_name, role, created_at, updated_at')
              .eq('id', item.trainer_id)
              .maybeSingle();
            
            if (!trainerError && trainerInfo) {
              trainerData = trainerInfo;
              console.log('✅ Fetched trainer data separately:', trainerData);
            } else if (trainerError) {
              console.error('❌ Error fetching trainer data:', trainerError);
              // If RLS blocks access, try to get minimal info from the relationship itself
              // We can at least show the trainer_id
              console.log('⚠️ RLS may be blocking trainer data access. Trainer ID:', item.trainer_id);
            }
          } catch (err) {
            console.error('❌ Exception fetching trainer data:', err);
          }
        }
        
        console.log('🔍 Processing item:', { 
          id: item.id, 
          trainer_id: item.trainer_id,
          trainerRaw: item.trainer,
          trainerProcessed: trainerData,
          trainerType: typeof trainerData,
          trainerKeys: trainerData ? Object.keys(trainerData) : 'null',
          trainerName: trainerData ? displayNameFromProfile(trainerData) : undefined,
          trainerEmail: trainerData?.email
        });
        
        return {
        id: item.id,
        trainerId: item.trainer_id,
        memberId: item.member_id,
        status: item.status,
        createdAt: item.created_at,
        startedAt: item.started_at,
        endedAt: item.ended_at,
        updatedAt: item.updated_at,
        requestedAt: item.requested_at,
        respondedAt: item.responded_at,
        lastContactDate: item.last_contact_date,
        nextCheckInDate: item.next_check_in_date,
        notes: item.notes,
        fitnessGoals: item.fitness_goals,
        initialAssessment: item.initial_assessment,
        contractType: item.contract_type,
        billingStatus: item.billing_status,
        monthlyRate: item.monthly_rate,
        assignedBy: item.assigned_by,
        terminationReason: item.termination_reason,
        memberRating: item.member_rating,
        memberReview: item.member_review,
        trainer: trainerData ? {
          id: trainerData.id,
          email: trainerData.email || '',
          first_name: typeof trainerData.first_name === 'string' ? trainerData.first_name : '',
          last_name: typeof trainerData.last_name === 'string' ? trainerData.last_name : '',
          role: trainerData.role || 'trainer',
          createdAt: trainerData.created_at || new Date().toISOString(),
          updatedAt: trainerData.updated_at || new Date().toISOString(),
        } as User : null as any,
      };
      }));
      
      return results;
    } catch (error) {
      console.error('Error in getPendingRequests:', error);
      throw error;
    }
  }

  /**
   * Accept a pending connection request
   */
  static async acceptRequest(relationshipId: string): Promise<void> {
    return this.updateRelationshipStatus(relationshipId, 'active');
  }

  /**
   * Decline a pending connection request (member said no).
   * Sets status to 'rejected' so it appears in the trainer's "Declined" tab (not Archived).
   * Requires DB migration: database_add_rejected_status.sql
   * @param reason Optional feedback reason (stored as termination_reason)
   */
  static async declineRequest(relationshipId: string, reason?: string): Promise<void> {
    const DECLINED_STATUS = 'rejected' as const;
    return this.updateRelationshipStatus(relationshipId, DECLINED_STATUS, reason);
  }
}

